<template>
  <div class="flex w-full items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
    
    <!-- LEFT SIDE: Toggle (Mobile Only) + Page Title -->
    <div class="flex items-center gap-x-4">
      <!-- Toggle Button: Hidden on desktop (md:hidden) -->
      <button 
        @click="$emit('toggle-sidebar')"
        class="md:hidden p-2 -ml-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      <h1 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
        {{ pageTitle }}
      </h1>
    </div>
    
    <!-- RIGHT SIDE: Actions -->
    <div class="flex items-center gap-x-2 sm:gap-x-4">
      
      <!-- Language Selector -->
      <div class="relative hidden sm:block">
        <select @change="$emit('change-lang', $event)" class="appearance-none w-28 cursor-pointer rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#035a76]">
          <option value="en">🇬🇧 English</option>
          <option value="fr">🇫🇷 Français</option>
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
        </div>
      </div>

      <!-- Dark Mode Toggle -->
      <button @click="$emit('toggle-dark-mode')" class="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <svg v-if="!isDarkMode" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
        <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      </button>
      
      <!-- Notifications -->
      <button class="relative p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
        <span class="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800"></span>
      </button>
      
      <!-- Divider -->
      <div class="hidden sm:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
      
      <!-- User Dropdown -->
      <div ref="userDropdownRef" class="relative">
        <button @click="isUserDropdownOpen = !isUserDropdownOpen" class="flex items-center gap-x-3 rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <div class="h-8 w-8 rounded-full bg-[#035a76] flex items-center justify-center text-sm font-semibold text-white shadow-sm">
            {{ getInitials(user?.name) }}
          </div>
          <div class="hidden md:flex flex-col items-start">
            <span class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{{ user?.name || "Guest" }}</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">Administrator</span>
          </div>
          <svg class="hidden md:block h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
        </button>
        
        <!-- Dropdown Menu -->
        <div v-if="isUserDropdownOpen" class="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-700">
          <a href="#" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Your Profile</a>
          <a href="#" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Settings</a>
          <div class="my-1 border-t border-gray-100 dark:border-gray-700"></div>
          <button @click.prevent="logout" class="flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
            Sign out
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@/composables/useAuth';

const { locale } = useI18n();
const { logout } = useAuth();

const props = defineProps({
  pageTitle: { type: String, default: 'Dashboard' },
  isDarkMode: { type: Boolean, default: false }
});

defineEmits(['toggle-sidebar', 'toggle-dark-mode', 'change-lang', 'logout']);

const user = ref(null);
const isUserDropdownOpen = ref(false);
const userDropdownRef = ref(null);

const handleClickOutside = (event) => {
  if (userDropdownRef.value && !userDropdownRef.value.contains(event.target)) {
    isUserDropdownOpen.value = false;
  }
};

onMounted(() => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) user.value = JSON.parse(storedUser);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
const changeLang = (event) => {
  locale.value = event.target.value;
};

const getInitials = (name) => {
  if (!name) return "G";
  const parts = name.trim().split(" ");
  return parts.length === 1 ? parts[0].charAt(0).toUpperCase() : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
</script>